import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn';

const control =
  'w-full rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 text-[15px] text-[var(--text)] placeholder:text-[var(--text-muted)]/70 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-red/60 focus:border-transparent disabled:opacity-60';

interface WrapProps {
  label: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
}

function Wrap({ id, label, error, hint, children }: WrapProps & { id: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-err`} role="alert" className="text-xs text-brand-red">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-[var(--text-muted)]">{hint}</p>
      ) : null}
    </div>
  );
}

export const TextField = forwardRef<HTMLInputElement, WrapProps & InputHTMLAttributes<HTMLInputElement>>(function TextField(
  { label, error, hint, icon, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Wrap id={fid} label={label} error={error} hint={hint}>
      <div className="relative">
        {icon && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">{icon}</span>}
        <input
          ref={ref}
          id={fid}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${fid}-err` : undefined}
          className={cn(control, 'h-12', icon ? 'pl-11' : '', error && 'border-brand-red', className)}
          {...rest}
        />
      </div>
    </Wrap>
  );
});

export const TextArea = forwardRef<HTMLTextAreaElement, WrapProps & TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea(
  { label, error, hint, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Wrap id={fid} label={label} error={error} hint={hint}>
      <textarea ref={ref} id={fid} aria-invalid={!!error || undefined} className={cn(control, 'py-3 min-h-24', className)} {...rest} />
    </Wrap>
  );
});

export const SelectField = forwardRef<HTMLSelectElement, WrapProps & SelectHTMLAttributes<HTMLSelectElement>>(function SelectField(
  { label, error, hint, className, id, children, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Wrap id={fid} label={label} error={error} hint={hint}>
      <select ref={ref} id={fid} aria-invalid={!!error || undefined} className={cn(control, 'h-12 appearance-none', className)} {...rest}>
        {children}
      </select>
    </Wrap>
  );
});
